// cSpell:ignore Bouwlaan boîte Scenariolaan Voorbeeldstraat
// The units of a group as a cost or a settlement splits over them.
//
// Archiving a unit does not undo the periods in which it was part of the
// group: its key weight and its tenants still count for those, and a vacant
// unit's share stays vacancy for its own owner. Dropping it shifted its share
// onto the tenants of the other units, without a word.

import type { IAddress } from "@rentiohq/shared-frontend/dist/types/property.types";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import {
  getLocalizedText,
  toLocale,
} from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import type { ApportionmentKey } from "./types";

// A unit as its building's costs and settlements name it: "bus 2" from its
// box number, else its own name. In `locale` (a contact's language, for a
// statement) when given, else in the reader's.
export const unitBoxLabel = (
  property: {
    code: string;
    box?: string;
  },
  locale?: string,
): string =>
  property.box?.trim()
    ? getLocalizedText(
        "costs.label.unit_box",
        { box: property.box.trim() },
        0,
        toLocale(locale),
      )
    : property.code;

// A property's address without its box, in two parts: "Scenariolaan 1" and
// "9000 Gent", as formatAddress writes them. Undefined without a street.
export interface IAddressParts {
  street: string;
  city?: string;
}

export const addressPartsOf = (
  address: IAddress,
): IAddressParts | undefined => {
  const street = formatAddress(
    { street: address.street, number: address.number },
    false,
    false,
  ).trim();
  if (!street) return undefined;
  const city = formatAddress({
    zip: address.zip,
    city: address.city,
    state: address.state,
    country: address.country,
  }).trim();
  return { street, ...(city ? { city } : {}) };
};

// A unit's full address with its box spelled out, "Scenariolaan 1 bus 3,
// 9000 Gent" (formatAddress writes "Scenariolaan 1 3", which reads as house
// number 13); the box word in `locale` (NL "bus", FR "boîte", EN "box"),
// else in the reader's language. A building, or a unit without a box, is
// its address alone. Without a known street, its label (unitBoxLabel).
export const unitAddressLabel = (
  property: { code: string; box?: string; addressParts?: IAddressParts },
  locale?: string,
): string => {
  const parts = property.addressParts;
  if (!parts) return unitBoxLabel(property, locale);
  const box = property.box?.trim();
  const street = box
    ? `${parts.street} ${unitBoxLabel({ code: property.code, box }, locale)}`
    : parts.street;
  return parts.city ? `${street}, ${parts.city}` : street;
};

// A unit of a building on screen (the cost popup's split, the payment run):
// its full address with the box spelled out, "Bouwlaan 10 bus 1, 9000 Gent",
// as the lists name it (M40); `fallback` when it has no street.
export const unitScreenLabel = (params: {
  property: IAddress & { box?: unknown };
  fallback: string;
}): string => {
  const { property, fallback } = params;
  const addressParts = addressPartsOf(property);
  if (!addressParts) return fallback;
  const box =
    typeof property.box === "string" && property.box.trim()
      ? property.box.trim()
      : undefined;
  return unitAddressLabel({ code: fallback, box, addressParts });
};

// A unit's full address as the property's header writes it (formatAddress:
// "Voorbeeldstraat 9 2, 9000 Gent"), from the property records a page
// already holds: the building's units first (an archived one by its plain
// address, which the property list would mark), else the office's property
// list. Undefined when neither knows the unit or its address is empty.
export const unitFullAddress = (params: {
  unitId?: string;
  units: { id: number | string; address?: string }[];
  properties: { id: string; address?: string }[];
}): string | undefined => {
  const { unitId, units, properties } = params;
  if (!unitId) return undefined;
  const address =
    units.find(unit => String(unit.id) === unitId)?.address ??
    properties.find(property => property.id === unitId)?.address;
  return address?.trim() || undefined;
};

export interface IArchivableUnit {
  // Calendar day the unit was archived; absent while it is active.
  archivedOn?: string;
}

// Whether a unit was part of the group on or after `from`: always while it is
// active, and once archived only when that happened on that day or later.
// Without a day (a cost whose period is not filled in yet) only an active unit
// counts.
export const existedSince = (params: {
  archivedOn?: string;
  from?: string;
}): boolean => {
  const { archivedOn, from } = params;
  return !archivedOn || (!!from && archivedOn >= from);
};

// The units that were part of the group on or after `from` (see existedSince).
export const unitsExistingSince = <TUnit extends IArchivableUnit>(params: {
  units: TUnit[];
  from?: string;
}): TUnit[] => {
  const { units, from } = params;
  return units.filter(unit =>
    existedSince({ archivedOn: unit.archivedOn, from }),
  );
};

// The units a group's costs split over, once per distinct set of units, in
// the order of the costs' first day. Each cost splits over the units that
// existed on its own first day (see unitsExistingSince), so two costs of one
// category can split over different units: a cost of 2026 leaves out a unit
// archived in 2025, a cost that began earlier still counts it. Each entry
// holds the stretch its costs cover together.
export const unitSplitsForCosts = <
  TUnit extends IArchivableUnit & { id: string },
>(params: {
  units: TUnit[];
  periods: { from: string; to: string }[];
}): { units: TUnit[]; from: string; to: string }[] => {
  const splits: { units: TUnit[]; from: string; to: string }[] = [];
  const unitIdsKey = (units: TUnit[]) => units.map(unit => unit.id).join(",");
  const byFirstDay = [...params.periods].sort((a, b) =>
    a.from.localeCompare(b.from),
  );
  for (const period of byFirstDay) {
    const units = unitsExistingSince({
      units: params.units,
      from: period.from,
    });
    const same = splits.find(
      split => unitIdsKey(split.units) === unitIdsKey(units),
    );
    if (!same) {
      splits.push({ units, from: period.from, to: period.to });
    } else if (period.to > same.to) {
      same.to = period.to;
    }
  }
  return splits;
};

// How a key splits a group's costs, once per distinct set of units (see
// unitSplitsForCosts): the weight of a unit and the basis it is divided by,
// as the engine divides. A key without weight for any of the units splits
// equally. Without `periods` the key splits over all units at once.
//
// An owner who sees their own part of a group knows only some of its units:
// `units` then holds those, and `allUnits` every unit of the group (without
// its label), so each split and its basis follow the units of each cost's
// period as for the broker, and each split lists the viewer's own units. A
// unit archived before a cost began takes no part in it there either: with
// the equal key, u1 reads "1 / 2" for a 2026 cost when u3 left in 2025, not
// "1 / 3". Older snapshots only give `unitCount`, the number of units of the
// group: the equal key's denominator, and the key's own basis for any other
// key.
export const keySplitsForCosts = <
  TUnit extends IArchivableUnit & { id: string },
>(params: {
  keyDef: Pick<ApportionmentKey, "isEqual" | "weights">;
  units: TUnit[];
  periods?: { from: string; to: string }[];
  allUnits?: (IArchivableUnit & { id: string })[];
  unitCount?: number;
}): {
  units: TUnit[];
  from?: string;
  to?: string;
  weightOf: (unitId: string) => number;
  total: number;
}[] => {
  const { keyDef } = params;
  const weights = keyDef.weights ?? {};
  const keyBasis = Object.values(weights).reduce(
    (sum, weight) => sum + (Number(weight) || 0),
    0,
  );
  const allUnits = params.allUnits ?? [];
  const hasAllUnits = allUnits.length > 0;
  const groupUnits: (IArchivableUnit & { id: string })[] = hasAllUnits
    ? allUnits
    : params.units;
  const unitCount = hasAllUnits ? undefined : params.unitCount;
  const splits: {
    units: (IArchivableUnit & { id: string })[];
    from?: string;
    to?: string;
  }[] =
    params.periods && params.periods.length > 0
      ? unitSplitsForCosts({ units: groupUnits, periods: params.periods })
      : [{ units: groupUnits }];
  return splits.map(split => {
    const { units: splitUnits, from, to } = split;
    const splitUnitIds = new Set(splitUnits.map(unit => unit.id));
    const units = params.units.filter(unit => splitUnitIds.has(unit.id));
    const isPartialView = unitCount != null && unitCount > splitUnits.length;
    const unitsBasis = splitUnits.reduce(
      (sum, unit) => sum + (Number(weights[unit.id]) || 0),
      0,
    );
    const splitsEqually =
      keyDef.isEqual ||
      (!isPartialView && splitUnits.length > 0 && unitsBasis <= 0);
    const weightOf = (unitId: string) =>
      splitsEqually ? 1 : Number(weights[unitId]) || 0;
    const basis =
      isPartialView || splitUnits.length === 0 ? keyBasis : unitsBasis;
    const total = splitsEqually
      ? Math.max(splitUnits.length, unitCount ?? 0)
      : basis;
    return { units, from, to, weightOf, total };
  });
};

// The first day a settlement can reach: its own period start, or an earlier
// start of one of its costs (each cost is split over its own period).
export const earliestDay = (days: (string | undefined)[]): string | undefined =>
  days.filter((day): day is string => !!day).sort()[0];

// The label of a unit in the screens of the feature, marked when the unit has
// been archived since.
export const unitDisplayLabel = (params: {
  label: string;
  archivedOn?: string;
}): string =>
  params.archivedOn
    ? getLocalizedText("costs.unit.archived_label", { label: params.label })
    : params.label;

export interface IUnitProperty {
  id: string;
  code: string;
  address?: string;
  type: "building" | "unit";
  parentId?: string;
  box?: string;
  // The address without its box, for unitAddressLabel.
  addressParts?: IAddressParts;
  // The property's office (broker): the sender of its statements.
  brokerId?: number;
}

// The office's properties plus the archived units a settlement still counts
// (the property list leaves archived ones out), so their tenants and owners
// are named by their address like any other unit. `marked` adds the archived
// mark for the wizard's own screens; a statement names the address only.
export const withArchivedUnits = (params: {
  properties: IUnitProperty[];
  units: (IArchivableUnit & { id: number; address: string })[];
  groupId?: string;
  marked: boolean;
}): IUnitProperty[] => {
  const { properties, units, groupId, marked } = params;
  const known = new Set(properties.map(property => property.id));
  const archived = units
    .filter(unit => unit.archivedOn && !known.has(String(unit.id)))
    .map(unit => {
      const label = marked
        ? unitDisplayLabel({ label: unit.address, archivedOn: unit.archivedOn })
        : unit.address;
      return {
        id: String(unit.id),
        code: label,
        address: label,
        type: "unit" as const,
        parentId: groupId,
      };
    });
  return archived.length > 0 ? [...properties, ...archived] : properties;
};

// The property ids among `ids` that the office's property list does not hold
// (an archived property is left out of it), each once.
export const unknownPropertyIds = (params: {
  properties: { id: string }[];
  ids: (string | undefined)[];
}): string[] => {
  const known = new Set(params.properties.map(property => property.id));
  return [
    ...new Set(
      params.ids.filter(
        (id): id is string =>
          !!id && !known.has(id) && !Number.isNaN(Number(id)),
      ),
    ),
  ];
};

// The group a unit belongs to. The unit's own property record decides: a
// unit keeps its group once archived (the group's settlements and costs still
// bill its former tenants), while the office's property list leaves it out.
// That list only answers while the record is not in yet.
export const unitParentGroupId = (params: {
  unitId?: string;
  ownParentId?: number | string | null;
  properties: { id: string; parentId?: string }[];
}): string | undefined => {
  const { unitId, ownParentId, properties } = params;
  if (!unitId) return undefined;
  if (ownParentId != null) return String(ownParentId);
  return properties.find(property => property.id === unitId)?.parentId;
};
